<template>
  <BaseImage
    v-if="formData.bgImage && formData.useImageRatio"
    :src="formData.bgImage"
    loading="lazy"
    class="w-full h-auto overflow-hidden rounded-xl"
  />
  <div
    class="flex px-6 md:px-8 overflow-hidden rounded-xl"
    :class="[
      flexAlignClass,
      formData.bgImage && formData.useImageRatio
        ? 'items-center absolute top-0 left-0 w-full h-full'
        : paddingClass,
    ]"
    :style="{
      background: !formData.bgImage ? formData.bgColor || '#000000' : undefined,
      color: formData.textColor,
      '--text-color': formData.textColor,
      ...useEditorFontFamily(formData.textFontFamily),
    }"
  >
    <BaseImage
      v-if="formData.bgImage && !formData.useImageRatio"
      :src="formData.bgImage"
      loading="lazy"
      class="absolute inset-0 w-full h-full object-cover rounded-xl"
    />
    <div
      class="flex flex-col gap-4 md:gap-8 w-full relative"
      :style="{
        maxWidth: formData.maxWidth ? `${formData.maxWidth}px` : '576px',
      }"
    >
      <BaseImage
        v-if="formData.logo"
        :src="formData.logo"
        loading="lazy"
        class="w-full h-auto"
        :class="logoAlignClass"
        :style="{
          maxWidth: (formData.logoWidth || 240) + 'px',
        }"
      />
      <div
        class="flex flex-col"
        :style="{
          gap: formData.slogan
            ? `${formData.sloganTextGap ?? 32}px`
            : undefined,
        }"
      >
        <h1
          v-if="formData.slogan"
          class="w-full"
          :class="[
            sloganFontSizeClass,
            `text-${sloganTextAlign}`,
            sloganContentAlignClass,
          ]"
          :style="{
            maxWidth: formData.sloganMaxWidth
              ? `${formData.sloganMaxWidth}px`
              : '576px',
            ...useEditorFontFamily(sloganFontFamily),
          }"
          v-html="sloganHtml"
        />
        <div
          class="marked"
          :class="[textFontSizeClass, `text-${formData.textAlign}`]"
          v-html="
            getMarkedTextWithBlankLinks(formData.text, formData.linkColor)
          "
        />
      </div>
      <div
        v-if="visibleButtons.length"
        class="flex flex-wrap gap-4"
        :class="flexAlignClass"
      >
        <template v-for="(button, index) in visibleButtons" :key="index">
          <EditorButton :button="button" @click="onButtonClick(button)" />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ButtonInterface } from '../common'
import type { HeroDisplayData } from './types'
import { sanitizeHeroSloganHtml } from './sanitize-html'
import EditorButton from '~/components/page-editor/EditorButton.vue'
import { TrackedLoginModalSourceDto } from '~/models/auth'
import { useAuth } from '~/stores/auth'
import { usePageEditor } from '~/stores/page-editor'

interface PropsInterface {
  formData: HeroDisplayData
  blockId: string
}

const props = defineProps<PropsInterface>()
const authStore = useAuth()
const pageEditorStore = usePageEditor()
const { getMarkedTextWithBlankLinks } = useMarkedText()

const isAuthButton = (button: ButtonInterface) =>
  button.type === 'registration' || button.type === 'login'

const visibleButtons = computed(
  () =>
    props.formData.buttons?.filter(
      (button) =>
        !isAuthButton(button) || !authStore.auth || pageEditorStore.isEditMode
    ) || []
)

const sloganTextAlign = computed(
  () => props.formData.sloganTextAlign || props.formData.textAlign || 'center'
)

const sloganFontFamily = computed(() =>
  props.formData.sloganFontFamily === 'custom'
    ? props.formData.sloganCustomFont
    : props.formData.sloganFontFamily
)

const sloganHtml = computed(() => {
  return sanitizeHeroSloganHtml(props.formData.slogan || '')
})

const sloganFontSizeClass = computed(() => {
  switch (props.formData.sloganFontSize) {
    case 'text-xl':
      return 'text-xl'
    case 'text-2xl':
      return 'text-2xl'
    case 'text-4xl':
      return 'text-4xl'
    case 'text-5xl':
      return 'text-5xl'
    case 'text-6xl':
      return 'text-6xl'
    default:
      return 'text-3xl'
  }
})

const textFontSizeClass = computed(() => {
  switch (props.formData.textFontSize) {
    case 'text-xl':
      return 'text-xl'
    case 'text-2xl':
      return 'text-2xl'
    case 'text-3xl':
      return 'text-3xl'
    case 'text-4xl':
      return 'text-4xl'
    case 'text-5xl':
      return 'text-5xl'
    case 'text-6xl':
      return 'text-6xl'
    default:
      return 'text-base'
  }
})

const onButtonClick = (button: ButtonInterface) => {
  if (!isAuthButton(button)) {
    return
  }

  authStore.openLoginModal(
    new TrackedLoginModalSourceDto('hero', {
      action: button.type as 'registration' | 'login',
      block_id: props.blockId,
      button_text: button.text,
    })
  )
}

const flexAlignClass = computed(() => {
  switch (props.formData.textAlign) {
    case 'left':
      return 'justify-start'
    case 'center':
      return 'justify-center'
    case 'right':
      return 'justify-end'
    default:
      return 'justify-center'
  }
})

const sloganContentAlignClass = computed(() => {
  switch (sloganTextAlign.value) {
    case 'center':
      return 'self-center'
    case 'right':
      return 'self-end'
    default:
      return 'self-start'
  }
})

const logoAlignClass = computed(() => {
  switch (props.formData.logoAlign) {
    case 'center':
      return 'self-center'
    case 'right':
      return 'self-end'
    default:
      return 'self-start'
  }
})

const paddingClass = computed(() => {
  switch (props.formData.paddingSize) {
    case 'xs':
      return 'py-6 md:py-8'
    case 'sm':
      return 'py-14 md:py-20'
    case 'lg':
      return 'py-32 md:py-56'
    default:
      return 'py-20 md:py-40'
  }
})
</script>
